import { useEffect, useState } from 'react';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { required } from 'helpers/validators';
import { IOption, IStatusOption, ITicket } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import SectionTitle from 'components/common/SectionTitle';
import { contractEquipmentService, ticketingService } from 'services/api';
import { DateTimeTextField } from 'components/common';
import { accessorService } from 'services/api/AccessorService';
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { useAppSelector } from 'app/store';
import { DateTimePickerAdapter } from 'components/form/DateTimePickerAdapter';
import { isDirector } from 'components/helpers/IsDirector';
import { IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';
import { MenuItem } from '@mui/material';

interface TicketFormParams {
  ticket: ITicket;
  onSubmit: any;
}
function TicketForm(params: TicketFormParams) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.tickets';

  const isDisabledEdit = isDirector();
  const isSuperAdmin = IsSuperAdminGuard();
  /** Data/ora editabili solo per amministratori; per i tecnici sono auto-generate */
  const canEditDates = isTenantAdministrator();

  const [equipments, setEquipments] = useState<IOption[]>([]);
  const [accessories, setAccessories] = useState<IOption[]>([]);
  const [selectedContractEquipment, setSelectedContractEquipment] = useState<IOption | null>(null);
  const [statusOptions, setStatusOptions] = useState<IStatusOption[]>([]);
  const [openDate, setOpenDate] = useState<Date>();
  const yesNoOptions = ['SI', 'NO'];
  const defaultWarehouse = useAppSelector((state) => state.auth.warehouse);

  useEffect(() => {
    const contract_params = {
      equipment_status: '1,2',
      ...(defaultWarehouse ? { warehouse_code: defaultWarehouse } : {})
    };
    contractEquipmentService.getAllBaseInformation(contract_params).then((res) => {
      const byEquipment = new Map<number, typeof res[number]>();
      for (const row of res || []) {
        const eid = row['equipment_id'] as number;
        if (eid != null && !byEquipment.has(eid)) {
          byEquipment.set(eid, row);
        }
      }
      setEquipments(
        Array.from(byEquipment.values()).map((eq) => {
          return {
            id: +eq['id'],
            code: `${eq['description']} (SN ${eq['serial_number']}) - (ID. ${eq['internal_id']})${
              eq['contract_code'] ? ` — Contr. ${eq['contract_code']}` : ''
            }`
          };
        })
      );
    });
    ticketingService.options().then((res) => {
      if (res.actions && res.actions.POST) {
        setStatusOptions(
          res.actions.POST.status.choices.map((item) => {
            return {
              ...item,
              display_name: t(`pages.tickets.status_options.${item.display_name}`)
            };
          })
        );
      }
    });
    accessorService.getAllPaginated(1000, 0).then((res) => {
      setAccessories(res?.results ?? []);
    });
  }, []);

  useEffect(() => {
    const equipmentId = (selectedContractEquipment as any)?.equipment_id;
    if (!equipmentId) {
      accessorService.getAllPaginated(1000, 0).then((res) => {
        setAccessories(res?.results ?? []);
      });
      return;
    }
    accessorService.getAllPaginated(1000, 0, { equipment_id: equipmentId }).then((res) => {
      setAccessories(res?.results ?? []);
    });
  }, [selectedContractEquipment]);

  const { ticket, onSubmit } = params;
  const statusKey = ticket?.status?.display_name ?? '';
  const isTechnicianResolved = statusKey === 'TECHNICIAN_RESOLVED';
  const isPhoneResolved = statusKey === 'PHONE_RESOLVED';
  const initialYesNoTechnician = isTechnicianResolved ? 'SI' : 'NO';
  const initialYesNoPhone = isPhoneResolved ? 'SI' : 'NO';

  const getStatusOptionValue = (key: string): number | undefined => {
    const opt = statusOptions.find((o) => o.display_name === key);
    return opt?.value;
  };
  const [createdAt, setCreatedAt] = useState<Date | undefined>();
  const [sentAt, setSentAt] = useState<Date | undefined>();
  const [specialistArrivalAt, setSpecialistArrivalAt] = useState<Date | undefined>();
  /** Campi editabili fino alla chiusura del ticket (non più disabilitati dopo il solo invio) */
  const isTicketClosed = Boolean(ticket?.resolved_at);
  const isFieldsDisabled = isDisabledEdit || (Boolean(ticket?.id) && isTicketClosed);

  /** Formato data/ora per visualizzazione sola lettura (tecnici) */
  const formatDateTime = (d: Date) => {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${h}:${min}`;
  };

  useEffect(() => {
    if (ticket?.created_at) {
      const [date, time] = ticket.created_at.split(' ');
      const [day, month, year] = date.split('/');
      const formattedDate = new Date(`${year}-${month}-${day}T${time}:00`);
      setCreatedAt(formattedDate);
    } else if (!ticket?.id) {
      // Nuovo ticket: precompila data/ora con ora corrente
      const now = new Date();
      setCreatedAt(now);
      setOpenDate(now);
    }
  }, [ticket]);

  useEffect(() => {
    if (ticket?.sent_at) {
      const [date, time] = ticket.sent_at.split(' ');
      const [day, month, year] = date.split('/');
      const formattedDate = new Date(`${year}-${month}-${day}T${time}:00`);
      setSentAt(formattedDate);
    } else {
      setSentAt(undefined);
    }
  }, [ticket]);

  useEffect(() => {
    if (ticket?.specialist_arrival_at) {
      const [date, time] = ticket.specialist_arrival_at.split(' ');
      const [day, month, year] = date.split('/');
      const formattedDate = new Date(`${year}-${month}-${day}T${time}:00`);
      setSpecialistArrivalAt(formattedDate);
    } else {
      setSpecialistArrivalAt(undefined);
    }
  }, [ticket]);

  return (
    <div>
      {ticket && (
        <ReactFinalForm
          onSubmit={(formValues) => {
            // UI SI/NO -> status
            const techYN = String(
              formValues.technician_resolved ?? initialYesNoTechnician
            ).toUpperCase();
            const phoneYN = String(formValues.phone_resolved ?? initialYesNoPhone).toUpperCase();
            const techYes = techYN === 'SI';
            const phoneYes = phoneYN === 'SI';

            if (techYes && !phoneYes) {
              const v = getStatusOptionValue('TECHNICIAN_RESOLVED');
              if (v != null) formValues.status = v;
            } else if (phoneYes && !techYes) {
              const v = getStatusOptionValue('PHONE_RESOLVED');
              if (v != null) formValues.status = v;
            } else if (!techYes && !phoneYes) {
              // se era in uno stato "risolto", riportalo ad OPEN (fallback)
              const current = ticket?.status?.display_name;
              if (current === 'TECHNICIAN_RESOLVED' || current === 'PHONE_RESOLVED') {
                const v = getStatusOptionValue('OPEN');
                if (v != null) formValues.status = v;
              }
            }

            // Date modificabili solo per amministratori: se tecnico non inviare created_at/sent_at
            if (canEditDates) {
              formValues.created_at =
                openDate ?? createdAt ?? (ticket?.id ? undefined : new Date());
              if (sentAt) formValues.sent_at = sentAt;
            } else {
              delete formValues.created_at;
              delete formValues.sent_at;
            }
            onSubmit(formValues);
          }}
          edit={!!ticket.id}
          hideControls={
            !isSuperAdmin &&
            !(['OPEN', 'INTERVENTION_REQUESTED'].indexOf(ticket.status.display_name) > -1)
          }>
          <div className="flex flex-wrap">
            <div className="w-1/3 p-4">
              {canEditDates ? (
                <IdentityField
                  name="created_at"
                  component={DateTimePickerAdapter}
                  showTime
                  label={t('entities.tickets.created_at')}
                  setValue={setOpenDate}
                  initialValue={createdAt}
                  type="date"
                />
              ) : (
                <DateTimeTextField
                  label={t('entities.tickets.created_at')}
                  value={ticket?.created_at ?? (createdAt ? formatDateTime(createdAt) : '')}
                  time
                  disabled
                />
              )}
            </div>
            {ticket.id && (
              <div className="w-1/3 p-4">
                {canEditDates ? (
                  <IdentityField
                    name="sent_at"
                    component={DateTimePickerAdapter}
                    showTime
                    label={t('entities.tickets.sent_at')}
                    setValue={setSentAt}
                    initialValue={sentAt}
                    type="date"
                  />
                ) : (
                  <DateTimeTextField
                    label={t('entities.tickets.sent_at')}
                    value={ticket?.sent_at ?? (sentAt ? formatDateTime(sentAt) : '')}
                    time
                    disabled
                  />
                )}
              </div>
            )}
            {ticket.resolved_at && (
              <div className="w-1/3 p-4">
                <DateTimeTextField
                  label={t(`entities.tickets.resolved_at`)}
                  value={ticket.resolved_at}
                  time
                  disabled
                />
              </div>
            )}
            {ticket.id && !ticket.resolved_at && (
              <div className="w-1/3 p-4">
                <IdentityField
                  name="specialist_arrival_at"
                  component={DateTimePickerAdapter}
                  showTime
                  label="Arrivo tecnico specialista (data/ora)"
                  initialValue={specialistArrivalAt}
                  disabled={isFieldsDisabled}
                  type="date"
                />
              </div>
            )}
            <div className="w-full p-2">
              <SectionTitle>{t('pages.supplier.new.registry')}</SectionTitle>
            </div>
            {!ticket.id && (
              <>
                <div
                  className="w-full md:w-1/2 p-2"
                  key={`contract_equipment_${equipments.length}`}>
                  <IdentityField
                    name="contract_equipment"
                    component={AutocompleteAdapter}
                    options={equipments}
                    placeholder={t('global.equipment')}
                    type="text"
                    initialValue={equipments.length === 1 ? equipments[0] : undefined}
                    setValue={setSelectedContractEquipment}
                  />
                </div>
                <div className="w-full md:w-1/2 p-2">
                  <IdentityField
                    key={`accessor_${selectedContractEquipment?.id ?? 'none'}`}
                    name="accessor"
                    component={AutocompleteAdapter}
                    placeholder={t('global.accessor')}
                    options={accessories}
                    type="text"
                    autoSelect={false}
                  />
                </div>
              </>
            )}

            {ticket.id && ticket.equipment && (
              <div className="w-full md:w-1/2 p-2">
                <IdentityField
                  name="contract_equipment"
                  component={TextFieldAdapter}
                  initialValue={`${ticket['equipment_description']} (SN ${ticket['serial_number']}) - (ID. ${ticket['internal_id']})`}
                  options={equipments}
                  placeholder={t('global.equipment')}
                  type="text"
                  disabled={true}
                />
              </div>
            )}

            {ticket.id && ticket.accessor && (
              <div className="w-full md:w-1/2 p-2">
                <IdentityField
                  name="accessor"
                  component={AutocompleteAdapter}
                  placeholder={t('global.accessor')}
                  label={t('global.accessor')}
                  options={accessories}
                  type="text"
                  disabled={true}
                />
              </div>
            )}
            {ticket.id && (
              <div className="p-2 w-full md:w-1/2">
                <IdentityField
                  name="status"
                  label={t(`global.status`)}
                  initValue={
                    ticket.status
                      ? {
                          ...ticket.status,
                          display_name: t(
                            `pages.tickets.status_options.${ticket.status.display_name}`
                          )
                        }
                      : {
                          value: 1,
                          display_name: t(`pages.tickets.status_options.OPEN`)
                        }
                  }
                  defaultValue={ticket.status ? ticket.status.value : 1}
                  component={AutocompleteAdapter}
                  options={statusOptions}
                  disabled={!isSuperAdmin}
                  type="text"
                  optionLabel="display_name"
                  optionValue="value"
                />
              </div>
            )}
            <div className="w-full p-2">
              <IdentityField
                name="description"
                initialValue={ticket.description}
                component={TextFieldAdapter}
                label="Problema riscontrato"
                type="text"
                validate={required}
                required
                disabled={isFieldsDisabled}
                multiline
                rows={8}
              />
            </div>
            <div className="w-full p-2">
              <IdentityField
                name="note"
                initialValue={ticket.note ? ticket.note : ''}
                component={TextFieldAdapter}
                label={t('global.notes')}
                type="text"
                disabled={isFieldsDisabled}
                multiline
                rows={4}
              />
            </div>
            <div className="w-full p-2">
              <SectionTitle>{t('pages.tickets.report-info')}</SectionTitle>
            </div>
            <div className="w-full p-2">
              <IdentityField
                name="code_error"
                initialValue={ticket.code_error ? ticket.code_error : ''}
                component={TextFieldAdapter}
                label={t('global.code_error')}
                placeholder={t('global.message-placeholder')}
                type="text"
                disabled={isFieldsDisabled}
              />
            </div>
            {/* STEP 1: Technician Activity */}
            <div className="w-full p-2">
              <SectionTitle>{t('pages.tickets.technician')}</SectionTitle>
            </div>
            <div className="p-2 w-full md:w-1/2 flex gap-2">
              <div className="w-1/3">
                <IdentityField
                  name="technician_resolved"
                  initialValue={initialYesNoTechnician}
                  component={TextFieldAdapter}
                  label="Risolto (SI/NO)"
                  disabled={isFieldsDisabled}
                  type="text"
                  select>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </IdentityField>
              </div>
              <div className="w-2/3">
                <IdentityField
                  name="technician_activity"
                  initialValue={ticket.technician_activity ? ticket.technician_activity : ''}
                  component={TextFieldAdapter}
                  label={t('global.activity')}
                  disabled={isFieldsDisabled}
                  type="text"
                />
              </div>
            </div>
            <div className="p-2 w-full md:w-1/2">
              <IdentityField
                name="technician_activity_description"
                initialValue={
                  ticket.technician_activity_description
                    ? ticket.technician_activity_description
                    : ''
                }
                component={TextFieldAdapter}
                label={t(`pages.tickets.activity-description`)}
                disabled={isFieldsDisabled}
                type="text"
                multiline
                rows={4}
              />
            </div>
            <div className="w-full p-2">
              <SectionTitle>{t('pages.tickets.phonecall')}</SectionTitle>
            </div>
            <div className="p-2 w-full md:w-1/2 flex gap-2">
              <div className="w-1/3">
                <IdentityField
                  name="phone_resolved"
                  initialValue={initialYesNoPhone}
                  component={TextFieldAdapter}
                  label="Risolto (SI/NO)"
                  disabled={isFieldsDisabled}
                  type="text"
                  select>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </IdentityField>
              </div>
              <div className="w-2/3">
                <IdentityField
                  name="phonecall_activity"
                  initialValue={ticket.phonecall_activity ? ticket.phonecall_activity : ''}
                  component={TextFieldAdapter}
                  label={t('global.activity')}
                  disabled={isFieldsDisabled}
                  type="text"
                />
              </div>
            </div>
            <div className="p-2 w-full md:w-1/2">
              <IdentityField
                name="phonecall_activity_description"
                initialValue={
                  ticket.phonecall_activity_description ? ticket.phonecall_activity_description : ''
                }
                component={TextFieldAdapter}
                label={t('pages.tickets.activity-description')}
                disabled={isFieldsDisabled}
                type="text"
                multiline
                rows={4}
              />
            </div>
            <div className="w-full p-2">
              <SectionTitle>{t('pages.tickets.spec_field')}</SectionTitle>
            </div>
            <div className="p-2 w-full md:w-1/2">
              <IdentityField
                name="spec_field_activity"
                initialValue={ticket.spec_field_activity ? ticket.spec_field_activity : ''}
                component={TextFieldAdapter}
                label="Richiesto intervento specialista (SI/NO)"
                disabled={isFieldsDisabled}
                type="text"
                select>
                {yesNoOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </IdentityField>
            </div>
            <div className="p-2 w-full md:w-1/2">
              <IdentityField
                name="spec_field_activity_description"
                initialValue={
                  ticket.spec_field_activity_description
                    ? ticket.spec_field_activity_description
                    : ''
                }
                component={TextFieldAdapter}
                label={t('pages.tickets.activity-description')}
                disabled={isFieldsDisabled}
                type="text"
                multiline
                rows={4}
              />
            </div>
          </div>
        </ReactFinalForm>
      )}
    </div>
  );
}

export default TicketForm;
